import React from 'react';
import { ShieldCheck, AlertTriangle, ShieldAlert } from 'lucide-react';

interface ScoreGaugeProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({ score, size = 'md', showIcon = true }) => {
  const strokeDashoffset = 283 - (283 * score) / 100;
  
  let color = '#10b981'; // emerald
  let statusText = 'Good';
  let Icon = ShieldCheck;
  let textColor = 'text-emerald-400';

  if (score < 60) {
    color = '#f43f5e'; // rose
    statusText = 'High risk';
    Icon = ShieldAlert;
    textColor = 'text-rose-400';
  } else if (score < 80) {
    color = '#f59e0b'; // amber
    statusText = 'Needs attention';
    Icon = AlertTriangle;
    textColor = 'text-amber-400';
  }

  const dimensions = {
    sm: { width: 90, height: 90, stroke: 7, text: 'text-2xl', label: 'text-[10px]' },
    md: { width: 140, height: 140, stroke: 10, text: 'text-4xl', label: 'text-xs' },
    lg: { width: 190, height: 190, stroke: 12, text: 'text-5xl', label: 'text-sm' },
  }[size];

  return (
    <div className="relative inline-flex flex-col items-center justify-center">
      <svg width={dimensions.width} height={dimensions.height} className="transform -rotate-90">
        <circle
          cx={dimensions.width / 2}
          cy={dimensions.height / 2}
          r={(dimensions.width - dimensions.stroke * 2) / 2}
          stroke="#1e293b"
          strokeWidth={dimensions.stroke}
          fill="transparent"
        />
        <circle
          cx={dimensions.width / 2}
          cy={dimensions.height / 2}
          r={(dimensions.width - dimensions.stroke * 2) / 2}
          stroke={color}
          strokeWidth={dimensions.stroke}
          strokeDasharray={283}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="transparent"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-extrabold tracking-tight ${dimensions.text} text-white`}>
          {score}
        </span>
        <span className="text-[10px] text-slate-400 font-medium -mt-0.5">/ 100</span>
      </div>
      {showIcon && (
        <div className={`mt-2 inline-flex items-center gap-1 font-semibold ${dimensions.label} ${textColor}`}>
          <Icon className="w-3.5 h-3.5" />
          <span>{statusText}</span>
        </div>
      )}
    </div>
  );
};
